import { VideoPlayer } from "@/_new_/components";
import {
  Alert,
  Box,
  Button,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import ReactPlayer from "react-player";

export type TutorVideoIntroductionProps = {
  handleClosingModal: () => void;
  getVideoDetails: () => Promise<{ verified: boolean; url: "string" }>;
  updateVideoStatus: (action: "approve" | "reject") => Promise<string>;
};
export const TutorVideoIntroduction = ({
  handleClosingModal,
  getVideoDetails,
  updateVideoStatus,
}: TutorVideoIntroductionProps) => {
  const [requestLoading, setRequestLoading] = useState(true);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState<"publish" | "reject" | "">("");
  const [success, setSuccess] = useState("");
  const [videoVerified, setVideoVerified] = useState(false);

  useEffect(() => {
    let mounted = true;

    (async () => {
      setError("");
      try {
        const data = await getVideoDetails();
        if (mounted) {
          setVideoUrl(data.url);
          setVideoVerified(data.verified);
        }
      } catch (e) {
        const message = (e as Error).message;
        mounted && setError(message ?? e);
      }
      setRequestLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleCTA = async (action: "publish" | "reject") => {
    if (!videoUrl) return;

    setLoading(action);
    setError("");
    try {
      if (action === "publish") {
        await updateVideoStatus("approve");
        setVideoVerified(true);
      } else {
        await updateVideoStatus("reject");
        setSuccess("Video rejected");
      }
    } catch (e) {
      const message = (e as Error).message;
      setError(message ?? "Error occurred while processing this action");
    }
    setLoading("");
  };

  useEffect(() => {
    let t: NodeJS.Timeout;
    if (success) {
      t = setTimeout(() => {
        setSuccess("");
      }, 3000);
    }

    return () => {
      clearTimeout(t);
    };
  }, [success]);

  return (
    <Modal isOpen={true} onClose={handleClosingModal} trapFocus={true}>
      <ModalOverlay />
      <ModalContent minW={"800px"} w="fit-content" py="1rem">
        <ModalCloseButton mt="1rem" />
        <ModalHeader>
          <Heading size="lg" mb="1rem">
            Review Video
          </Heading>
        </ModalHeader>
        <ModalBody>
          {requestLoading ? (
            <Box display="flex" justifyContent={"center"} alignItems={"center"}>
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color="blue.500"
                size="xl"
              />
            </Box>
          ) : (
            <Box>
              {videoUrl && (
                <Box roundedTop="lg" mb="1rem">
                  <ReactPlayer
                    controls={true}
                    playing={true}
                    url={videoUrl}
                    width="100%"
                  />
                </Box>
              )}
            </Box>
          )}
          {error && (
            <Text mb="1rem" fontSize="sm" color="tomato">
              {error}
            </Text>
          )}
          {success && (
            <Text mb="1rem" fontSize="sm" color="#3caf50">
              {success}
            </Text>
          )}
        </ModalBody>
        <ModalFooter display={"block"}>
          {videoVerified && (
            <Alert status="info" mb="1rem" rounded={"md"}>
              <Text fontSize="sm">This video has been approved</Text>
            </Alert>
          )}
          <Box
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
            w={"full"}
          >
            <Button
              colorScheme={"red"}
              type="button"
              isLoading={loading === "reject"}
              loadingText="Rejecting..."
              onClick={() => handleCTA("reject")}
            >
              Reject
            </Button>
            <Button
              colorScheme={"green"}
              type="submit"
              isLoading={loading === "publish"}
              loadingText="Publishing"
              onClick={() => handleCTA("publish")}
              disabled={videoVerified}
            >
              Publish
            </Button>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
